import { Gender, IdentityDocumentType } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { isoDateString } from '../_shared/dates.js';
import { withRenamedDeprecatedKey } from '../_shared/deprecated-key.js';
import { personName } from '../_shared/person-name.js';
/**
 * IGA pvt accounts selectable when importing/creating a booking from Affidium.
 * WEB (site-web) is reserved for autonomous checkout bookings and is never
 * chosen here. Defaults to the booking's agency first (RUN -> WSMASSILIA2),
 * with Marseille bookings still using WSMASSILIA2 for RUN departures.
 * WSMASSILIA4 (MASSILIA B2B) is auto-picked when the client is a COMPANY.
 */
export const igaPvtAccountSchema = z.enum(['WSMASSILIA1', 'WSMASSILIA2', 'WSMASSILIA4']);
/** All IGA accounts, including WEB (autonomous checkout). */
export const igaAccountSchema = z.enum(['WEB', 'WSMASSILIA1', 'WSMASSILIA2', 'WSMASSILIA4']);
/**
 * Human labels for the IGA portefeuilles. Single source of truth: le formulaire
 * de création (choix du compte) et la fiche dossier (compte de synchronisation)
 * doivent nommer un portefeuille de la même façon.
 */
export const IGA_ACCOUNT_LABELS = {
    WEB: 'Site web',
    WSMASSILIA1: 'Marseille',
    WSMASSILIA2: 'Réunion',
    WSMASSILIA4: 'Massilia B2B',
};
export function resolveAffidiumImportIgaAccount({ agency, clientType, departureLocation, }) {
    if (clientType === 'COMPANY')
        return 'WSMASSILIA4';
    if (agency === 'RUN' || departureLocation === 'RUN')
        return 'WSMASSILIA2';
    return 'WSMASSILIA1';
}
/**
 * Resolve a client's IGA customer code for a given IGA account. Codes are
 * per-account (each account is a distinct portefeuille), so the value shown for a
 * booking must match that booking's `igaAccountId`. Shared by the server (invoice
 * PDF) and the backoffice (booking detail) to keep resolution consistent.
 */
export function resolveClientIgaCode(igaCodes, account) {
    return igaCodes.find((c) => c.account === account)?.code ?? null;
}
const affidiumPassengerPatchSchema = z.object({
    travellerId: z.string().min(1),
    birthDate: isoDateString(),
    gender: z.nativeEnum(Gender),
    identityDocumentExpiryDate: isoDateString().nullish(),
    identityDocumentNumber: z.string().nullish(),
    identityDocumentType: z.nativeEnum(IdentityDocumentType).nullish(),
    issuingCountry: z.string().length(3).nullish(),
    nationalityCode: z.string().length(3),
    price: z.number().optional(),
});
// `installmentPaymentDeadlineOverride` est l'ancien nom de `paymentDeadlineOverride` :
// alias déprécié, à supprimer à la release suivante.
export const createBookingFromAffidiumSchema = withRenamedDeprecatedKey(z.object({
    clientId: z.string().optional(),
    igaAccountId: igaPvtAccountSchema.optional(),
    reservationId: z.string().optional(),
    accountCode: z.string().optional(),
    bookingReference: z.string().min(1),
    depositAmountOverride: z.number().optional(),
    depositDeadlineOverride: isoDateString().optional(),
    exchangeAllowed: z.boolean(),
    exchangeFeeDescription: z.string(),
    igaReference: z.string().optional(),
    installmentChosen: z.boolean().optional(),
    passengers: z.array(affidiumPassengerPatchSchema).optional(),
    paymentDeadlineOverride: isoDateString().optional(),
    refundAllowed: z.boolean(),
    refundFeeDescription: z.string(),
    sendReservationContract: z.boolean().optional(),
    /**
     * Dossier sans suivi : la vente a été conclue à l'agence, on l'inscrit pour
     * les statistiques et le mailing, et rien d'autre ne s'y applique. Réservé à
     * l'import par PNR — un dossier né d'un devis a traversé tout le cycle de
     * vente, acompte compris, la case n'y aurait aucun sens.
     */
    untracked: z.boolean().optional(),
}), 'installmentPaymentDeadlineOverride', 'paymentDeadlineOverride');
export const reconcileBookingPassengerItemSchema = z.object({
    bookingPassengerId: z.string().min(1),
    firstName: personName({ optional: true }),
    lastName: personName({ optional: true }),
    targetPassengerId: z.string().min(1).optional(),
    action: z.enum(['CREATE', 'LINK', 'SKIP']),
    birthDate: isoDateString().optional(),
    gender: z.nativeEnum(Gender).optional(),
    nationalityCode: z.string().min(2).optional(),
});
export const reconcileBookingPassengersSchema = z.object({
    dryRun: z.boolean(),
    items: z.array(reconcileBookingPassengerItemSchema).optional(),
});
export const updateBookingPassengerSchema = z.object({
    firstName: personName({ optional: true }),
    lastName: personName({ optional: true }),
    birthDate: isoDateString().nullish(),
    canceled: z.boolean().optional(),
    gender: z.nativeEnum(Gender).optional(),
    identityDocumentExpiryDate: isoDateString().nullish(),
    identityDocumentNumber: z.string().nullish(),
    identityDocumentType: z.nativeEnum(IdentityDocumentType).nullish(),
    issuingCountry: z.string().length(3).nullish(),
    nationalityCode: z.string().length(3).optional(),
    price: z.number().int().min(0).optional(),
});
